import { DeliveryType } from '../enums/pitches.enums';
import { Nullable } from '../interfaces/common/i-generics';
import { IStereoVideoMetadata } from '../interfaces/i-stereo-video';
import { IVideo, IVideoError } from '../interfaces/i-video';
import { IPitchVideos } from '../interfaces/pitches';
import { IPosition } from '../interfaces/pitches/i-base';
import { isNumber } from './math.utilities';

/** earliest in any video that we allow the release time to be */
const MIN_RELEASE_TIME_SEC = 1;

/** min frame in any video that we allow the release frame to be */
const MIN_RELEASE_FRAME = 30;

const MAX_VIDEO_DURATION_S = 10;

/** max difference between release time and expected release time after converting release frame to seconds using video fps */
const MAX_DELTA_RELEASE_TIME_FRAME_SEC = 0.5;

const VALIDATION_TOLERANCE = {
  /** feet */
  X: { value: 2, text: '2 ft' },
  /** feet */
  Z: { value: 1, text: '1 ft' },
};

type PitchWithVideoID = IPitchVideos & {
  video_id: Nullable<string>; // * this is from the old interface shape; we no longer want to use video_id
};

export class VideoHelper {
  static validOrigin = (metadata: IStereoVideoMetadata) =>
    [
      metadata.origin_px.x,
      metadata.origin_px.y,
      metadata.pixels_per_foot.x,
      metadata.pixels_per_foot.z,
    ].every((n) => isNumber(n));

  static getPitchDeliveryVideoID = (
    pitch: Partial<PitchWithVideoID>,
    delivery: DeliveryType | undefined
  ) => {
    switch (delivery) {
      case DeliveryType.Stretch: {
        if (pitch.stretchVideoID === undefined) {
          // has never been set, fallback to video_id
          return pitch.video_id;
        }

        // might be null but should no longer fallback to video_id
        return pitch.stretchVideoID;
      }

      case DeliveryType.Windup:
      default: {
        if (pitch.windupVideoID === undefined) {
          // has never been set, fallback to video_id
          return pitch.video_id;
        }

        // might be null but should no longer fallback to video_id
        return pitch.windupVideoID;
      }
    }
  };

  static getOptionLabel = (video: IVideo) => {
    return video.VideoTitle?.trim() ?? video.VideoFileName ?? 'Untitled';
  };

  static getAspectRatio = (video: IVideo) => {
    try {
      return (video.cap_size_1 / (video.cap_size_0 || 1)).toFixed(2);
    } catch {
      return '1.6';
    }
  };

  static getFatalErrors = (video: Partial<IVideo>): IVideoError[] =>
    this.getErrors(video).filter((e) => e.fatal);

  /** returns a list of errors (if any) that prevent the video from being used, empty list if there are no issues detected */
  static getErrors = (video: Partial<IVideo>): IVideoError[] => {
    const output: IVideoError[] = [
      // {
      //   msg: 'Test Error',
      //   fatal: false,
      // },
      ...VideoHelper.getCompletenessErrors(video),
      ...VideoHelper.getValidityErrors(video as IVideo),
    ];

    return output;
  };

  static getCompletenessErrors(video: Partial<IVideo>): IVideoError[] {
    try {
      const output: IVideoError[] = [];

      if (!video.ffmpeg) {
        output.push({
          fatal: true,
          msg: 'FFMPEG metadata is not defined',
        });
      }

      if (!isNumber(video.n_frames)) {
        output.push({
          fatal: true,
          msg: 'Total frames is not a number',
        });
      } else if (video.n_frames === 0) {
        output.push({
          fatal: true,
          msg: 'Total frames is zero',
        });
      }

      if (!isNumber(video.ReleasePixelX)) {
        output.push({
          fatal: true,
          msg: 'Release Pixel X is not a number',
        });
      }

      if (!isNumber(video.ReleasePixelY)) {
        output.push({
          fatal: true,
          msg: 'Release Pixel Y is not a number',
        });
      }

      if (!isNumber(video.MoundPixelX)) {
        output.push({
          fatal: true,
          msg: 'Mound Pixel X is not a number',
        });
      }

      if (!isNumber(video.MoundPixelY)) {
        output.push({
          fatal: true,
          msg: 'Mound Pixel Y is not a number',
        });
      }

      const parsedRT = parseFloat(video.ReleaseTime as any);
      if (!isNumber(parsedRT)) {
        output.push({
          fatal: true,
          msg: 'Release Time is not a number',
        });
      }

      if (!isNumber(video.ReleaseSide)) {
        output.push({
          fatal: true,
          msg: 'Release Side is not a number',
        });
      }

      if (!isNumber(video.ReleaseHeight)) {
        output.push({
          fatal: true,
          msg: 'Release Height is not a number',
        });
      }

      return output;
    } catch (e) {
      return [
        {
          fatal: true,
          msg:
            e instanceof Error
              ? e.message
              : 'Unknown error while checking video completeness',
        },
      ];
    }
  }

  private static getValidityErrors(video: IVideo): IVideoError[] {
    try {
      const output: IVideoError[] = [];

      if (video.ffmpeg.duration.seconds > MAX_VIDEO_DURATION_S) {
        output.push({
          fatal: false,
          msg: `Video duration should not exceed ${MAX_VIDEO_DURATION_S} seconds`,
        });
      }

      if (
        video.ReleasePixelY < 0 ||
        video.ReleasePixelY > video.ffmpeg.video.resolution.h
      ) {
        output.push({
          fatal: true,
          msg: `Release Pixel Y (${video.ReleasePixelY}) is not between 0 and ${video.ffmpeg.video.resolution.h}`,
        });
      }

      if (
        video.ReleasePixelX < 0 ||
        video.ReleasePixelX > video.ffmpeg.video.resolution.w
      ) {
        output.push({
          fatal: true,
          msg: `Release Pixel X (${video.ReleasePixelX}) is not between 0 and ${video.ffmpeg.video.resolution.w}`,
        });
      }

      if (
        video.MoundPixelY < 0 ||
        video.MoundPixelY > video.ffmpeg.video.resolution.h
      ) {
        output.push({
          fatal: true,
          msg: `Mound Pixel Y (${video.MoundPixelY}) is not between 0 and ${video.ffmpeg.video.resolution.h}`,
        });
      }

      if (
        video.MoundPixelX < 0 ||
        video.MoundPixelX > video.ffmpeg.video.resolution.w
      ) {
        output.push({
          fatal: true,
          msg: `Mound Pixel X (${video.MoundPixelX}) is not between 0 and ${video.ffmpeg.video.resolution.w}`,
        });
      }

      if (video.ReleasePixelY > video.MoundPixelY) {
        output.push({
          fatal: true,
          msg: `Release Pixel Y (${video.ReleasePixelY}) is greater than Mound Pixel Y (${video.MoundPixelY})`,
        });
      }

      if (video.ReleaseTime < MIN_RELEASE_TIME_SEC) {
        output.push({
          fatal: true,
          msg: `Release Time (${video.ReleaseTime.toFixed(
            3
          )}s) is less than the minimum (${MIN_RELEASE_TIME_SEC}s)`,
        });
      }

      if (
        Math.abs(
          video.ReleaseFrame / video.ffmpeg.video.fps - video.ReleaseTime
        ) > MAX_DELTA_RELEASE_TIME_FRAME_SEC
      ) {
        output.push({
          fatal: true,
          msg: `Release Time (${video.ReleaseTime.toFixed(
            3
          )}s) is inconsistent with Release Frame (${
            video.ReleaseFrame
          }) and FPS (${video.ffmpeg.video.fps})`,
        });
      }

      if (video.ReleaseFrame > video.n_frames) {
        output.push({
          fatal: true,
          msg: `Release Frame (${video.ReleaseFrame}) is greater than the max value (${video.n_frames})`,
        });
      }

      if (video.ReleaseFrame < MIN_RELEASE_FRAME) {
        output.push({
          fatal: true,
          msg: `Release Frame (${video.ReleaseFrame}) is less than the min value (${MIN_RELEASE_FRAME})`,
        });
      }

      return output;
    } catch (e) {
      return [
        {
          fatal: true,
          msg:
            e instanceof Error
              ? e.message
              : 'Unknown error while checking video validity',
        },
      ];
    }
  }

  /** returns warnings (e.g. when there's a large discrepancy between video release and pitch release) */
  // todo: deprecate if we are for sure never showing this warning anymore
  static validateSelection(config: {
    position: IPosition;
    video: IVideo;
    /** used for errors if provided */
    pitch_name?: string;
  }): string[] {
    const output: string[] = [];

    if (
      Math.abs(config.position.px - config.video.ReleaseSide) >
      VALIDATION_TOLERANCE.X.value
    ) {
      output.push(
        `Release side (${config.position.px.toFixed(1)} ft) ${
          config.pitch_name ? `for "${config.pitch_name}" ` : ''
        }is more than ${
          VALIDATION_TOLERANCE.X.text
        } off of video release side (${config.video.ReleaseSide.toFixed(
          1
        )} ft).`
      );
    }

    if (
      Math.abs(config.position.pz - config.video.ReleaseHeight) >
      VALIDATION_TOLERANCE.Z.value
    ) {
      output.push(
        `Release height (${config.position.pz.toFixed(1)} ft) ${
          config.pitch_name ? `for "${config.pitch_name}" ` : ''
        }is more than ${
          VALIDATION_TOLERANCE.Z.text
        } off of video release height (${config.video.ReleaseHeight.toFixed(
          1
        )} ft).`
      );
    }

    return output;
  }

  static convertTimeToFrame = (config: {
    time: number;
    fps: number;
    maxFrame: number;
  }): number => {
    if (isNaN(config.time)) {
      console.warn('Cannot use NaN time, returning -1');
      return -1;
    }

    if (isNaN(config.fps)) {
      console.warn('Cannot use NaN fps, returning -1');
      return -1;
    }

    if (config.time < 0) {
      console.warn('Cannot use negative time, returning -1');
      return -1;
    }

    if (config.fps < 0) {
      console.warn('Cannot use negative fps, returning -1');
      return -1;
    }

    return Math.min(
      config.maxFrame,
      Math.max(1, Math.floor(config.time * config.fps))
    );
  };

  static convertFrameToTime = (config: {
    frame: number;
    fps: number;
    maxFrame: number;
  }): number => {
    if (isNaN(config.frame)) {
      console.warn('Cannot use NaN frame, returning -1');
      return -1;
    }

    if (isNaN(config.fps)) {
      console.warn('Cannot use NaN fps, returning -1');
      return -1;
    }

    if (config.frame < 0) {
      console.warn('Cannot use negative frame, returning -1');
      return -1;
    }

    if (config.fps < 0) {
      console.warn('Cannot use negative fps, returning -1');
      return -1;
    }

    if (config.fps === 0) {
      console.warn('Cannot use 0 fps, returning -1');
      return -1;
    }

    return Math.min(config.maxFrame, Math.max(1, config.frame)) / config.fps;
  };
}
